import { toast } from '@/components/ui/use-toast';
import { isSessionNotifiedError } from '@/lib/api';
import { logError } from '@/utils/logger';

/** Matches ImpersonationReadOnlyFilter.READ_ONLY_ERROR on the backend. */
const READ_ONLY_ERROR = 'Modo lectura';

export interface ApiError {
  error?: string;
  message?: string;
  details?: string;
  status?: number;
}

/** HTTP status from an axios-like error (avoids relying on `isAxiosError` across bundles). */
export const getAxiosErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const raw = (error as { response?: { status?: number | string } }).response
    ?.status;
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return typeof n === 'number' && !Number.isNaN(n) ? n : undefined;
};

/**
 * Handles errors globally and shows appropriate toasts
 * @param error - The error to handle
 * @param customTitle - Custom title (optional)
 * @param customDescription - Custom description (optional)
 */
export const handleGlobalError = (
  error: unknown,
  customTitle?: string,
  customDescription?: string,
) => {
  logError('Global error handler', error);

  // The interceptor already told the user why this one failed — a support
  // session that expired — and its toast is the accurate one. The caller's copy
  // describes the screen it was on ("no se pudieron cargar los torneos"), not
  // what happened, and with TOAST_LIMIT = 1 it would replace the real reason
  // instead of queueing behind it.
  //
  // Keyed on "notified", not on "handled": the cross-session replay guard also
  // marks its rejections handled but shows nothing itself, and suppressing the
  // caller there would leave a silent failure with no retry cue.
  if (isSessionNotifiedError(error)) {
    return;
  }

  const httpStatus = getAxiosErrorStatus(error);

  // The read-only 403 is the actual UX of the support session: write buttons are
  // not hidden across the manager — that is exactly the cost this design avoids —
  // so this rejection is what the admin sees when they touch something. It has to
  // stay above the friendly-copy branch below, which fires on any 401/403 as soon
  // as the caller passed both a title and a description (every backoffice screen
  // does) and would replace this with "no se pudo completar la acción", leaving
  // the admin without knowing it was read-only mode.
  const responseData = (error as { response?: { data?: ApiError } })?.response
    ?.data;
  if (httpStatus === 403 && responseData?.error === READ_ONLY_ERROR) {
    toast({
      title: responseData.error,
      description: responseData.message ?? '',
      variant: 'destructive',
    });
    return;
  }

  // Never show raw API bodies for auth failures when the caller passed friendly copy
  if (
    (httpStatus === 401 || httpStatus === 403) &&
    customTitle &&
    customDescription
  ) {
    toast({
      title: customTitle,
      description: customDescription,
      variant: 'destructive',
    });
    return;
  }

  // If it's an axios error with response
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { data?: ApiError } };
    const errorData = axiosError.response?.data;

    if (errorData && typeof errorData === 'object') {
      // If it has error and message, use error as title and message as description
      if (errorData.error && errorData.message) {
        toast({
          title: errorData.error,
          description: errorData.message,
          variant: 'destructive',
        });
        return;
      }

      // If it only has message, use as title
      if (errorData.message) {
        toast({
          title: errorData.message,
          description: errorData.details || customDescription || 'Ha ocurrido un error inesperado',
          variant: 'destructive',
        });
        return;
      }

      // If it only has error, use as title
      if (errorData.error) {
        toast({
          title: errorData.error,
          description: customDescription || 'Ha ocurrido un error inesperado',
          variant: 'destructive',
        });
        return;
      }
    }
  }

  // If it's a standard Error
  if (error instanceof Error) {
    toast({
      title: customTitle || 'Error',
      description: error.message,
      variant: 'destructive',
    });
    return;
  }

  // If it's a string
  if (typeof error === 'string') {
    toast({
      title: customTitle || 'Error',
      description: error,
      variant: 'destructive',
    });
    return;
  }

  // Generic error - use custom values as fallbacks
  toast({
    title: customTitle || 'Error',
    description: customDescription || 'Ha ocurrido un error inesperado',
    variant: 'destructive',
  });
};

/**
 * Wrapper for async functions that handles errors automatically
 * @param asyncFn - Async function to execute
 * @param customTitle - Custom title for errors
 * @param customDescription - Custom description for errors
 */
export const withErrorHandling = async <T>(
  asyncFn: () => Promise<T>,
  customTitle?: string,
  customDescription?: string,
): Promise<T | null> => {
  try {
    return await asyncFn();
  } catch (error) {
    handleGlobalError(error, customTitle, customDescription);
    return null;
  }
};
